import React, { PropsWithChildren, useContext, useMemo, useState } from "react";
import { BotFormContext } from "@shared/features/bots/BotForm/common/BotForm.context";
import useSharedDCAStrategyForm from "@shared/features/bots/BotForm/Strategies/useSharedDCAStrategyForm";
import useSharedCustomStrategyForm from "@shared/features/bots/BotForm/Strategies/useSharedCustomStrategyForm";
import { OrderTypes } from "@shared/features/trades/common/types";
import {
  useUpdateFormStrategyAndErrors,
  useUpdateStrategyPriceError,
} from "@shared/features/bots/BotForm/common/hooks";
import { BotType, ConfigurationItem, BuiltInStrategyData } from "@shared/types";
import { BotStrategiesContext } from "@shared/features/bots/BotForm/Strategies/BotStrategies.context";
import {
  getCreateItem,
  getItemByName,
} from "@shared/features/configuration/utils";
import {
  CUSTOM_STRATEGIES_ITEM_NAME,
  DCA_STRATEGIES_ITEM_NAME,
} from "@shared/features/configuration/constants";
import {
  getBotTemplateStrategies,
  getConfigurationItem,
} from "@shared/features/bots/utils";
import useInitializeBotStrategies from "@shared/features/bots/BotForm/Strategies/useInitializeBotStrategies";
import { StrategyTabs } from "@shared/features/bots/BotForm/common/types";
import { isHedgingRangeStrategyMixed } from "@shared/features/bots/BotForm/common/utils";

interface BotStrategiesProviderProps extends PropsWithChildren {
  orderType: OrderTypes.ENTRY | OrderTypes.TAKE_PROFIT;
  createCustomStrategyTemplate?: ConfigurationItem;
  includeNameSelect?: boolean;
  customStrategies?: ConfigurationItem[];
  shouldInitializeTargets?: boolean;
  selectedTab?: StrategyTabs;
  tab?: StrategyTabs;
}

const BotStrategiesProvider = ({
  orderType,
  createCustomStrategyTemplate: propsCreateCustomStrategyTemplate,
  includeNameSelect,
  children,
  customStrategies,
  shouldInitializeTargets,
  selectedTab,
  tab,
}: BotStrategiesProviderProps) => {
  const {
    fieldsToInitialize,
    botGeneralData,
    botType,
    hedgingMode,
    entryStrategy,
    hedgingGridsData,
  } = useContext(BotFormContext) || {};
  const [initialized, setInitialized] = useState(false);
  const isCustomStrategy = tab === StrategyTabs.CUSTOM;
  const isFocused = selectedTab === tab;

  const strategiesItem = getConfigurationItem(
    botGeneralData,
    orderType === OrderTypes.ENTRY ? "Entry Strategy" : "Take-Profit Strategy"
  );
  const customStrategiesItem = getItemByName(
    CUSTOM_STRATEGIES_ITEM_NAME,
    strategiesItem
  );
  const dcaStrategiesItem = getItemByName(
    DCA_STRATEGIES_ITEM_NAME,
    strategiesItem
  );
  const defaultCreateCustomStrategyTemplate =
    getCreateItem(customStrategiesItem);
  const createCustomStrategyTemplate =
    propsCreateCustomStrategyTemplate ?? defaultCreateCustomStrategyTemplate;
  const createDcaStrategyTemplate = getCreateItem(dcaStrategiesItem);

  const templateStrategies = useMemo(() => {
    return getBotTemplateStrategies(botGeneralData, orderType);
  }, [botGeneralData, orderType]);

  const [templateStrategy, setTemplateStrategy] = useState<
    BuiltInStrategyData["builtin_strategy_type"] | undefined
  >(undefined);

  const {
    nameInputData,
    errors: customStrategyErrors,
    targetsData,
    numTargetsSelectorData,
    initializeValues: initializeCustomStrategyValues,
    isValid: isCustomStrategyValid,
    strategyModified: customStrategyModified,
  } = useSharedCustomStrategyForm({
    customStrategyItem: createCustomStrategyTemplate,
    includeNameSelect: includeNameSelect,
    customStrategies,
    shouldInitializeTargets,
  });

  const {
    parsedData: parsedDcaData,
    initializeValues: initializeDcaStrategyValues,
    isValid: isDcaStrategyValid,
    numOrdersSelectorData,
    numActiveOrdersSelectorData,
    firstOrderPercentSelectorData,
    amountScaleSelectorData,
    priceDifferenceSelectorData,
    priceScaleSelectorData,
    baselineSelectorData,
    maxPriceDifferenceData,
  } = useSharedDCAStrategyForm({
    orderType,
    isCustomStrategy,
    targets: targetsData.parsedTargets,
    createDcaStrategyTemplate,
  });

  const isPreservingMixedHedgingRangeStrategy =
    botType === BotType.HEDGING &&
    orderType === OrderTypes.ENTRY &&
    isHedgingRangeStrategyMixed({
      hedgingMode,
      entryStrategy,
      hedgingGridsData,
    });
  const isValid =
    isPreservingMixedHedgingRangeStrategy ||
    tab === StrategyTabs.BUILT_IN ||
    (isDcaStrategyValid && (!isCustomStrategy || isCustomStrategyValid));
  const fieldName =
    orderType === OrderTypes.ENTRY ? "entryStrategy" : "takeProfitStrategy";
  const shouldInitializeField = fieldsToInitialize?.has(fieldName);

  useUpdateStrategyPriceError(
    orderType,
    !isPreservingMixedHedgingRangeStrategy &&
      maxPriceDifferenceData?.shouldDisplay &&
      !!maxPriceDifferenceData.error,
    maxPriceDifferenceData?.hasConsecutiveGridsDistanceError,
    isFocused
  );

  useUpdateFormStrategyAndErrors({
    orderType,
    isFocused,
    isValid,
    tab,
    shouldUpdate:
      initialized &&
      !shouldInitializeField &&
      !isPreservingMixedHedgingRangeStrategy,
    targets: targetsData.targets,
    parsedDcaData,
    templateStrategy,
  });
  useInitializeBotStrategies({
    orderType,
    tab,
    selectedTab,
    initialized,
    setInitialized,
    initializeCustomStrategyValues,
    initializeDcaStrategyValues,
    createCustomStrategyTemplate,
    createDcaStrategyTemplate,
    templateStrategies,
    setTemplateStrategy,
  });

  return (
    <BotStrategiesContext.Provider
      value={{
        orderType,
        numOrdersSelectorData,
        numActiveOrdersSelectorData,
        firstOrderPercentSelectorData,
        amountScaleSelectorData,
        priceDifferenceSelectorData,
        priceScaleSelectorData,
        baselineSelectorData,
        isCustomStrategy,
        isValid,
        isCustomStrategyValid,
        nameInputData,
        customStrategyErrors,
        numTargetsSelectorData,
        targetsData,
        customStrategyModified,
        customStrategiesItem,
        dcaStrategiesItem,
        createCustomStrategyTemplate,
        createDcaStrategyTemplate,
        templateStrategies,
        templateStrategy,
        setTemplateStrategy,
        tab,
        selectedTab,
      }}
    >
      {children}
    </BotStrategiesContext.Provider>
  );
};

export default BotStrategiesProvider;
